import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';

import { Store } from '@ngrx/store';
import { TransactionConfiguration } from 'projects/semeris/src/generated/graphql-models';
import { BehaviorSubject, concat, merge, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, share, switchMap, tap } from 'rxjs/operators';

import { Transaction } from '../../../home/models/transaction.model';
import { TransactionService } from '../../../home/services/transaction.service';
import { Label } from '../../../label/models/label.model';
import { Manager } from '../../../manager/models/manager.model';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { WarningComponent } from '../../../shared/components/warning/warning.component';
import { ConfirmDirective } from '../../../shared/directives/confirm.directive';
import { LowerCaseInputDirective } from '../../../shared/directives/lowercase.directive';
import { NgIfVersionDirective } from '../../../shared/directives/ng-if-version.directive';
import { IFormControl } from '../../../shared/models/i-form/i-form-control.model';
import { IFormGroup } from '../../../shared/models/i-form/i-form-group.model';
import { BoldSpanPipe } from '../../../shared/pipes/bold-span.pipe';
import { FormControlErrorPipe } from '../../../shared/pipes/form-control-error.pipe';
import { SafeHtmlPipe } from '../../../shared/pipes/safe-html.pipe';
import { UserDateTimeFormatPipe } from '../../../shared/pipes/user-date-time-format.pipe';
import { getEnvironment } from '../../../shared/utils/get-environment';
import { searchByProperties } from '../../../shared/utils/search-by-properties';
import { ButtonDirective } from '../../../ui/button/button/button.directive';
import { TransactionTaskTableComponent } from '../../components/transaction-task-table/transaction-task-table.component';
import { TransactionTaskResponse, TransactionTaskTableItem } from '../../models/transaction-task.model';
import { TransactionTaskService } from '../../services/transaction-task.service';
import { TransactionTaskStore } from '../../store/transaction-task.store';

export interface PublishDealOptions {
  managers: Manager[];
  managerIdByTransactionId: Record<string, string>;
  transactionId?: string;
  managerId?: string;
}

export interface PublishDealModalResult {
  docBundleId: string;
  publishToProdusb: boolean;
  publishToProd: boolean;
  publishToDev: boolean;
  publishToStaging: boolean;
  clearDatafactoryAndSemtexFiles: boolean;
  documentShortName?: string;
}

interface PublishDealForm {
  publishToProdusb: boolean;
  publishToProd: boolean;
  publishToDev: boolean;
  publishToStaging: boolean;
  transactionId: string;
  managerId: string;
  clearDatafactoryAndSemtexFiles: boolean;
  documentShortName?: string;
}

@Component({
  selector: 'semeris-publish-deal-modal',
  templateUrl: './publish-deal-modal.component.html',
  styleUrls: ['./publish-deal-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    MatIcon,
    LoaderComponent,
    MatDialogContent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    LowerCaseInputDirective,
    MatError,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    NgIfVersionDirective,
    MatSlideToggle,
    MatRadioGroup,
    MatRadioButton,
    MatDialogActions,
    ButtonDirective,
    MatDialogClose,
    AsyncPipe,
    BoldSpanPipe,
    SafeHtmlPipe,
    FormControlErrorPipe,
    TransactionTaskTableComponent,
    ConfirmDirective,
    WarningComponent,
    UserDateTimeFormatPipe,
    MatDivider,
  ],
  providers: [TransactionTaskStore],
})
export class PublishDealModalComponent implements OnInit, AfterViewInit {
  protected readonly data = inject<PublishDealOptions>(MAT_DIALOG_DATA);
  private readonly transactionService = inject(TransactionService);
  private readonly transactionTaskService = inject(TransactionTaskService);
  private readonly dialogRef = inject<MatDialogRef<PublishDealModalComponent>>(MatDialogRef);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly transactionTaskStore = inject(TransactionTaskStore);

  public form = new IFormGroup<PublishDealForm>({
    publishToProdusb: new IFormControl(false),
    publishToProd: new IFormControl(false),
    publishToStaging: new IFormControl(
      getEnvironment() === 'staging' || getEnvironment() === 'production' || getEnvironment() === 'produsb'
    ),
    publishToDev: new IFormControl(false),
    clearDatafactoryAndSemtexFiles: new IFormControl(false),
    transactionId: new IFormControl(undefined, [Validators.required, Validators.pattern(/^[a-z0-9-]+\.[a-z0-9-]+$/)]),
    managerId: new IFormControl(undefined, [Validators.required, Validators.pattern(/^[a-z0-9-]+$/)]),
    documentShortName: new IFormControl(null),
  });

  public loading$ = new BehaviorSubject<boolean>(false);
  public error$ = new BehaviorSubject<string>(undefined);
  public documentShortNames$ = new BehaviorSubject<string[]>([]);
  public isTransactionReadyForPublish$: Observable<boolean>;
  public managerOptions$: Observable<{ value: string; text: string }[]>;

  public manualTrigger$ = new Subject<void>();
  public selectedLabels: Label[] = [];
  public taskLoading = signal(false);
  public taskCreatedId = signal<string | undefined>(undefined);
  public task = computed<TransactionTaskTableItem>(
    () => this.transactionTaskStore.tableItems()?.items?.[0] ?? this.transactionTaskStore.tableItems()?.pinnedItems?.[0]
  );

  protected docBundleId$ = new BehaviorSubject<string>(undefined);

  public ngOnInit(): void {
    const validation$ = merge(
      this.manualTrigger$,
      this.form.controls.managerId.valueChanges,
      this.form.controls.transactionId.valueChanges
    ).pipe(
      filter(() => this.form.valid),
      debounceTime(500),
      filter(
        () => !!this.transaction?.libraryId && !!this.transaction?.transactionShortName && !!this.transaction?.managerId
      ),
      tap(() => {
        this.loading$.next(true);
      }),
      switchMap(() => {
        const transaction = this.transaction;
        return this.transactionService
          .getTransactionConfiguration(transaction.transactionShortName, transaction.libraryId, transaction.managerId)
          .pipe(
            catchError(() =>
              of<TransactionConfiguration>({
                documentShortNames: [],
                error: 'Network error. Please try again.',
              })
            )
          );
      }),
      tap((config) => {
        this.loading$.next(false);
        this.docBundleId$.next(config?.docBundleId);
        this.error$.next(config.error);
        this.form.controls.documentShortName.setValue(null, { emitEvent: false });
        this.documentShortNames$.next(config?.documentShortNames || []);
      }),
      map((config) => !config?.error),
      share()
    );

    this.updateTask(
      this.docBundleId$.pipe(
        filter((docBundleId) => !!docBundleId),
        switchMap((docBundleId) => this.transactionTaskService.getLatestTransactionTaskByDocBundleId(docBundleId)),
        takeUntilDestroyed(this.destroyRef)
      )
    );

    this.isTransactionReadyForPublish$ = concat(
      of(false),
      merge(
        this.form.controls.managerId.valueChanges.pipe(map(() => false)),
        this.form.controls.transactionId.valueChanges.pipe(map(() => false)),
        validation$
      )
    );

    this.form.controls.transactionId.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((val) => this.data.managerIdByTransactionId && !!this.data.managerIdByTransactionId[val])
      )
      .subscribe((val) => {
        this.form.controls.managerId.setValue(this.data.managerIdByTransactionId[val]);
      });

    this.managerOptions$ = concat(
      of(this.data.managers),
      this.form.controls.managerId.valueChanges.pipe(
        map(() =>
          searchByProperties(this.data.managers, ['managerDisplayName', 'managerId'], this.form.getRawValue().managerId)
        )
      )
    ).pipe(
      map((managers) =>
        managers.map((m) => ({
          text: `${m.managerId} (${m.managerDisplayName})`,
          value: m.managerId,
        }))
      )
    );
  }

  public ngAfterViewInit(): void {
    if (this.data.managerId && this.data.transactionId) {
      this.form.patchValue(
        {
          managerId: this.data.managerId,
          transactionId: this.data.transactionId,
        },
        {
          emitEvent: true,
        }
      );

      this.manualTrigger$.next(undefined);
    }
  }

  public save() {
    this.dialogRef.close(this.result);
  }

  private get result(): PublishDealModalResult {
    return {
      publishToStaging: this.form.controls.publishToStaging.value,
      publishToProd: this.form.controls.publishToProd.value,
      publishToProdusb: this.form.controls.publishToProdusb.value,
      publishToDev: this.form.controls.publishToDev.value,
      clearDatafactoryAndSemtexFiles: this.form.controls.clearDatafactoryAndSemtexFiles.value,
      docBundleId: this.docBundleId$.value,
      documentShortName: this.form.controls.documentShortName.value || undefined,
    };
  }

  private get transaction(): Partial<Transaction> {
    const value = this.form.getRawValue();
    const [libraryId, transactionShortName] = value.transactionId.split('.');

    return {
      transactionId: value.transactionId,
      managerId: value.managerId,
      libraryId,
      transactionShortName,
    };
  }

  private updateTask(task$: Observable<TransactionTaskResponse>) {
    this.taskLoading.set(true);
    this.transactionTaskStore.updateTransactionTasks(
      task$.pipe(
        tap(() => {
          this.taskLoading.set(false);
          this.transactionTaskStore.setVisibleColumns(['statusLabels', 'labels', 'analysts', 'reviewers', 'comment']);
        }),
        map((task) => [task]),
        catchError(() => {
          this.taskLoading.set(false);
          return [];
        })
      )
    );
  }

  public onTaskCreate() {
    const docBundleId = this.docBundleId$.value;
    if (docBundleId) {
      this.updateTask(
        this.transactionTaskService.createTransactionTask(docBundleId).pipe(
          tap((task) => {
            this.taskCreatedId.set(task?._id);
          })
        )
      );
    }
  }
}
